import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Text, Flex, Toggle, Message, Modal, Box, InjectedModalProps, CloseIcon, ChevronLeftIcon } from 'uikit'
import {
  // useAudioModeManager,
  useExpertModeManager,
  useUserTransactionTTL,
  useUserSlippageTolerance,
  useUserSingleHopOnly,
} from 'state/user/hooks'
import { useTranslation } from 'contexts/Localization'

import { useSwapActionHandlers } from 'state/swap/hooks'
import { AutoColumn } from '../../Layout/Column'
import QuestionHelper from '../../QuestionHelper'
import { RowFixed } from '../../Layout/Row'
import TransactionSettings from './TransactionSettings'

const StyledModal = styled(Modal)`
  background: #0e1838;
  border: 1px solid #8900ff;
  box-sizing: border-box;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  & > div {
    padding: 0;
  }
`

const StyledMessage = styled(Message)`
  background: rgba(137, 0, 255, 0.15);
  border: 1px solid #9112ff;
  box-sizing: border-box;
  border-radius: 10px;
`

const SettingsModal: React.FC<InjectedModalProps> = ({ onDismiss }) => {
  const [showConfirmExpertModal, setShowConfirmExpertModal] = useState(false)
  const [userSlippageTolerance, setUserslippageTolerance] = useUserSlippageTolerance()
  const [ttl, setTtl] = useUserTransactionTTL()
  const [expertMode, toggleExpertMode] = useExpertModeManager()
  const [singleHopOnly, setSingleHopOnly] = useUserSingleHopOnly()
  // const [audioPlay, toggleSetAudioMode] = useAudioModeManager()
  const { onChangeRecipient } = useSwapActionHandlers()

  const { t } = useTranslation()

  if (showConfirmExpertModal) {
    return (
      <StyledModal
        hideHeader
        title={t('Are you sure?')}
        // onBack={() => setShowConfirmExpertModal(false)}
        // onDismiss={onDismiss}
        style={{ maxWidth: '418px' }}
      >
        <Box p="24px">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center">
              <Button
                height="30px"
                width="30px"
                padding="0"
                variant="text"
                onClick={() => setShowConfirmExpertModal(false)}
                area-label="go back"
              >
                <ChevronLeftIcon width="30px" fill="#9112FF" />
              </Button>
              <Text fontSize="20px" fontWeight="600" color="primary">
                {t('Are you sure?')}
              </Text>
            </Flex>
            <Button
              height="20px"
              width="20px"
              padding="0"
              variant="text"
              onClick={onDismiss}
              aria-label="Close the dialog"
            >
              <CloseIcon fill="#9112FF" />
            </Button>
          </Flex>
          <StyledMessage variant="warning" mb="16px" mt="30px">
            <Text fontSize="14px" fontWeight="300">
              {t(
                "Expert mode turns off the 'Confirm' transaction prompt, and allows high slippage trades that often result in bad rates and lost funds.",
              )}
            </Text>
          </StyledMessage>
          <Text mb="32px" fontSize="14px" fontWeight="300">
            {t('Only use this mode if you know what you’re doing.')}
          </Text>
          <Button
            borderRadius={5}
            width="100%"
            id="confirm-expert-mode"
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.prompt(`Please type the word "confirm" to enable expert mode.`) === 'confirm') {
                toggleExpertMode()
                setShowConfirmExpertModal(false)
              }
            }}
          >
            {t('Turn On Expert Mode')}
          </Button>
        </Box>
      </StyledModal>
    )
  }

  return (
    <StyledModal hideHeader title="Settings" onDismiss={onDismiss} style={{ minWidth: '418px' }}>
      <AutoColumn gap="md" style={{ padding: '24px' }}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="20px" fontWeight="600" color="primary">
            {t('Settings')}
          </Text>
          <Button
            height="20px"
            width="20px"
            padding="0"
            variant="text"
            onClick={onDismiss}
            aria-label="Close the dialog"
          >
            <CloseIcon fill="#9112FF" />
          </Button>
        </Flex>
        <Text bold fontSize="20px" fontWeight="500" mt="12px" color="textTertiary">
          {t('Transaction Settings')}
        </Text>
        <TransactionSettings
          rawSlippage={userSlippageTolerance}
          setRawSlippage={setUserslippageTolerance}
          deadline={ttl}
          setDeadline={setTtl}
        />
        <Text bold fontSize="20px" fontWeight="500" mt="32px" color="textTertiary">
          {t('Interface Settings')}
        </Text>
        <Flex pr="20px" justifyContent="space-between">
          <RowFixed>
            <Text fontSize="15px" color="textTertiary" fontWeight="300">
              {t('Toggle Expert Mode')}
            </Text>
            <QuestionHelper
              text={t('Bypasses confirmation modals and allows high slippage trades. Use at your own risk.')}
              ml="4px"
            />
          </RowFixed>
          <Toggle
            scale="sm"
            id="toggle-expert-mode-button"
            checked={expertMode}
            onChange={
              expertMode
                ? () => {
                    onChangeRecipient(null)
                    toggleExpertMode()
                  }
                : () => setShowConfirmExpertModal(true)
            }
          />
        </Flex>
        <Flex pr="20px" justifyContent="space-between">
          <RowFixed>
            <Text fontSize="15px" color="textTertiary" fontWeight="300">
              {t('Disable Multihops')}
            </Text>
            <QuestionHelper text={t('Restricts swaps to direct pairs only.')} ml="4px" />
          </RowFixed>
          <Toggle
            scale="sm"
            id="toggle-disable-multihop-button"
            checked={singleHopOnly}
            onChange={() => {
              setSingleHopOnly(!singleHopOnly)
            }}
          />
        </Flex>
        {/* <Flex justifyContent="space-between" alignItems="center" mb="8px">
          <RowFixed>
            <Text fontSize="14px">{t('Audio')}</Text>
            <QuestionHelper text={t('🐰 Turn down your volume a bit before you swap')} ml="4px" />
          </RowFixed>
          <PancakeToggle checked={audioPlay} onChange={toggleSetAudioMode} />
        </Flex> */}
      </AutoColumn>
    </StyledModal>
  )
}

export default SettingsModal
